"use client"

import { useCallback, useEffect, useState } from "react"

export type CandidateAction = "maybe" | "hidden" | null

interface SearchActionsData {
  searchId: string
  hidden: string[]
  maybe: string[]
}

const STORAGE_KEY = "candidate_actions"

function safeReadAll(): SearchActionsData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function safeWriteAll(all: SearchActionsData[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch {
    // ignore quota/private-mode errors
  }
}

function getForSearch(all: SearchActionsData[], searchId: string): SearchActionsData {
  return all.find((s) => s.searchId === searchId) ?? { searchId, hidden: [], maybe: [] }
}

function upsert(all: SearchActionsData[], entry: SearchActionsData): SearchActionsData[] {
  const others = all.filter((s) => s.searchId !== entry.searchId)

  // normalize: dedupe + prevent overlap (hidden wins)
  const hiddenSet = new Set(entry.hidden)
  const maybeSet = new Set(entry.maybe)
  for (const id of hiddenSet) maybeSet.delete(id)

  const normalized: SearchActionsData = {
    searchId: entry.searchId,
    hidden: Array.from(hiddenSet),
    maybe: Array.from(maybeSet),
  }

  return [...others, normalized]
}

export function useCandidateActions(searchId: string) {
  const [actions, setActions] = useState<SearchActionsData>(() => {
    return getForSearch(safeReadAll(), searchId)
  })

  /*
    The search id arrives a beat after the page does — the results route reads
    it off `/search/queries` — and a `useState` initialiser only ever runs once.
    Without this, opening a result page directly showed every hidden candidate
    again until you hid something, at which point the store finally keyed on the
    real search.
  */
  useEffect(() => {
    setActions(getForSearch(safeReadAll(), searchId))
  }, [searchId])

  // single helper: read -> mutate -> write -> sync state
  const commit = useCallback(
    (mutate: (current: SearchActionsData) => SearchActionsData) => {
      const all = safeReadAll()
      const current = getForSearch(all, searchId)
      const next = mutate(current)
      const updatedAll = upsert(all, next)
      safeWriteAll(updatedAll)
      setActions(getForSearch(updatedAll, searchId))
    },
    [searchId],
  )

  const setAction = useCallback(
    (candidateId: string, action: CandidateAction) => {
      commit((current) => {
        const hidden = current.hidden.filter((id) => id !== candidateId)
        const maybe = current.maybe.filter((id) => id !== candidateId)

        if (action === "hidden") hidden.push(candidateId)
        if (action === "maybe") maybe.push(candidateId)

        return { searchId: current.searchId, hidden, maybe }
      })
    },
    [commit],
  )

  const clearAction = useCallback(
    (candidateId: string) => {
      commit((current) => ({
        searchId: current.searchId,
        hidden: current.hidden.filter((id) => id !== candidateId),
        maybe: current.maybe.filter((id) => id !== candidateId),
      }))
    },
    [commit],
  )

  const clearSearch = useCallback(() => {
    commit((current) => ({ searchId: current.searchId, hidden: [], maybe: [] }))
  }, [commit])

  const getAction = useCallback(
    (candidateId: string): CandidateAction => {
      if (actions.hidden.includes(candidateId)) return "hidden"
      if (actions.maybe.includes(candidateId)) return "maybe"
      return null
    },
    [actions.hidden, actions.maybe],
  )

  const getCounts = useCallback(() => {
    return { maybeCount: actions.maybe.length, hiddenCount: actions.hidden.length }
  }, [actions.maybe.length, actions.hidden.length])

  const getAllIds = useCallback(() => {
    return { hidden: actions.hidden, maybe: actions.maybe }
  }, [actions.hidden, actions.maybe])

  const filterCandidates = useCallback(
    <T extends { profile?: { public_identifier?: string } }>(
      candidates: T[],
      includeHidden = false,
    ): T[] => {
      if (includeHidden) return candidates

      return candidates.filter((c) => {
        const id = c.profile?.public_identifier
        if (!id) return true
        return getAction(id) !== "hidden"
      })
    },
    [getAction],
  )

  return {
    setAction,
    getAction,
    clearAction,
    getCounts,
    filterCandidates,

    clearSearch,
    getAllIds,
    actions,
  }
}
